import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowDown } from 'lucide-react'
import { MessageBubble } from './message-bubble'
import { UserProps } from '@/entities/user'
import { ConversationProps } from '@/entities/conversation'
import { message_status, MessageProps } from '@/entities/message'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'
import { FileViewer } from '@/components/dialogs/file-preview'
import { FilePreviewDialog } from '@/components/dialogs/file-preview-dialog'

interface ChatWindowProps {
  conversation: ConversationProps | null
  user: UserProps | null
  onDeleteMessage: (messageRef: string, conversationRef: string) => void
  isLoadingMessage: boolean
  hasAddedFiles: boolean
  streamingMsgRef?: string | null
  streamingDelta?: string
  streamStatus?: 'idle' | 'streaming' | 'done' | 'failed'
  onRegenerate?: (lastUserContent: string, ninaMsgRef: string) => void
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ conversation, user, onDeleteMessage, isLoadingMessage, hasAddedFiles, streamingMsgRef, streamingDelta, streamStatus, onRegenerate }: ChatWindowProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const isMdAndDown = useMediaQuery('(max-width:1123px)')
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false)
  const [previewFile, setPreviewData] = useState<string>('')
  const [isAtBottom, setIsAtBottom] = useState<boolean>(true)

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior })
  }

  useEffect(() => {
    const target = messagesEndRef.current
    if (!target) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setIsAtBottom(entry.isIntersecting)
      },
      { root: null, threshold: 0, rootMargin: '0px 0px -120px 0px' }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (isAtBottom || isLoadingMessage) {
      scrollToBottom('smooth')
    }
  }, [conversation?.messages, isLoadingMessage])

  useEffect(() => {
    if (!streamingDelta) return
    if (!isAtBottom) return
    const raf = requestAnimationFrame(() => scrollToBottom('auto'))
    return () => cancelAnimationFrame(raf)
  }, [streamingDelta, isAtBottom])

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4 text-center ">
        <p className="text-gray-500 dark:text-gray-400">Selecione uma conversa ou inicie um novo bate-papo</p>
      </div>
    )
  }

  return (
    <div className={cn('flex-1 justify-center', isMdAndDown ? (hasAddedFiles ? 'pb-40' : 'pb-24') : hasAddedFiles ? 'pb-32' : 'pb-12')}>
      {conversation?.messages?.length === 0 ? (
        <motion.div
          className="flex flex-col items-center justify-center h-full text-center gap-2"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } }}
        >
          <motion.p
            className="text-gray-500 dark:text-gray-400"
            variants={{ hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } } }}
          >
            Nenhuma mensagem ainda. Inicie a conversa!
          </motion.p>
        </motion.div>
      ) : (
        <div className="space-y-4 md:space-y-6">
          <AnimatePresence initial={false}>
            {(() => {
              const filteredMessages = (conversation?.messages ?? []).filter((message: MessageProps) => !!message?.ref)
              const lastIndex = filteredMessages.length - 1
              return filteredMessages.map((message: MessageProps, i: number) => {
                const isLastMsg = i === lastIndex
                const isNinaMsg = message.senderId == null
                const isStreaming = streamingMsgRef === message.ref && streamStatus === 'streaming'
                const isThinking = isNinaMsg && message.status === 'PROCESSING' && !message.content && !isStreaming

                const handleRegenerate = isLastMsg && isNinaMsg && !isThinking && !isStreaming && onRegenerate
                  ? () => {
                      // Find the most recent user message before this Nina message
                      const prevUserMsg = [...filteredMessages].slice(0, i).reverse().find(m => m.senderId != null)
                      if (prevUserMsg?.content) {
                        onRegenerate(prevUserMsg.content, message.ref)
                      }
                    }
                  : undefined

                return (
                  <motion.div
                    key={message.ref ?? i}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="flex flex-row items-end justify-end gap-2">
                      {message?.paths?.length > 0 &&
                        message.paths?.map((path: string, index: number) => {
                          if (path.length > 0) {
                            return (
                              <div key={index} className="w-auto max-w-[150px] justify-end rounded-2xl cursor-pointer">
                                <FileViewer
                                  url={path}
                                  className="min-w-46 max-w-46 max-h-32 object-cover"
                                  onFileClick={() => {
                                    setPreviewData(path)
                                    setIsPreviewOpen(true)
                                  }}
                                />
                              </div>
                            )
                          }
                        })}
                    </div>
                    <MessageBubble
                      message={message}
                      user={user}
                      onDelete={messageRef => onDeleteMessage(messageRef, conversation?.ref)}
                      isStreaming={isStreaming}
                      streamedContent={streamingMsgRef === message.ref ? streamingDelta : undefined}
                      isLast={isLastMsg}
                      onRegenerate={handleRegenerate}
                    />
                  </motion.div>
                )
              })
            })()}

            {isLoadingMessage && (
              <motion.div
                key="loading"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              >
                <MessageBubble isLoadingMessage={isLoadingMessage} />
              </motion.div>
            )}
          </AnimatePresence>

          <div ref={messagesEndRef} />
        </div>
      )}

      <AnimatePresence>
        {!isAtBottom && (conversation?.messages?.length ?? 0) > 0 && (
          <motion.button
            type="button"
            onClick={() => scrollToBottom()}
            initial={{ opacity: 0, y: 12, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.92 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              'fixed z-30 h-10 w-10 rounded-full bg-background border border-border shadow-lg',
              'flex items-center justify-center text-foreground/80 hover:text-foreground hover:bg-accent',
              'transition-colors duration-150 ease-out',
              isMdAndDown ? 'bottom-32 right-4' : 'bottom-40 right-8'
            )}
            aria-label="Ir para a última mensagem"
          >
            <ArrowDown className="h-5 w-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {isPreviewOpen && <FilePreviewDialog open={isPreviewOpen} url={previewFile || ''} onClose={() => setIsPreviewOpen(false)} />}
    </div>
  )
}
